import React, { useContext, useState, useEffect } from "react";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import axios from "../../axiosConfig";
import { TrainerContext } from "../../context/TrainerContext";
import { AiOutlineDelete } from "react-icons/ai";
import { FaChevronDown, FaChevronUp, FaUsers } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import "../../styles/TrainerConfirmedTrainings.css";

dayjs.locale("pl");

/**
 * Component for displaying confirmed upcoming training sessions
 * Supports both individual and group trainings with filtering and search
 */
const TrainerConfirmedTrainings = ({ data }) => {
    const { trainer } = useContext(TrainerContext);

    // State management
    const [trainersList, setTrainersList] = useState([]);
    const [selectedTrainerId, setSelectedTrainerId] = useState(trainer?.id || null);
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedTrainingId, setExpandedTrainingId] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [trainingToDelete, setTrainingToDelete] = useState(null);

    /**
     * Fetch all trainers associated with the school
     */
    useEffect(() => {
        if (!trainer?.schoolId) return;

        const token = localStorage.getItem("jwt");

        axios
            .get(`/api/v1/trainer/find-all/${trainer.schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                const trainers = response.data || [];
                setTrainersList(trainers);

                if (!selectedTrainerId && trainers.length > 0) {
                    setSelectedTrainerId(trainer.id);
                }
            })
            .catch((error) => {
                console.error("Error fetching trainers:", error);
            });
    }, [trainer, selectedTrainerId]);

    /**
     * Handle trainer tab selection
     * @param {string} trainerId - Selected trainer's ID
     */
    const handleTrainerSelection = (trainerId) => {
        setSelectedTrainerId(trainerId);
        setExpandedTrainingId(null);
    };

    /**
     * Format date and time for display
     * @param {string} dateTime - ISO date string
     * @returns {string} Formatted date and time
     */
    const formatDateTime = (dateTime) => {
        return dayjs(dateTime).format("DD.MM.YYYY HH:mm");
    };

    /**
     * Toggle training details expansion
     * @param {string} trainingId - Training ID to toggle
     */
    const toggleTrainingDetails = (trainingId) => {
        setExpandedTrainingId(
            expandedTrainingId === trainingId ? null : trainingId
        );
    };

    /**
     * Handle training deletion confirmation
     * @param {string} trainingId - Training ID to delete
     */
    const handleDeleteTrainingConfirmation = (trainingId) => {
        const token = localStorage.getItem("jwt");

        axios
            .post(
                `/api/v1/training/${trainingId}/decline`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            )
            .then(() => {
                alert("Trening usunięty pomyślnie.");
                setIsDeleteModalOpen(false);
                window.location.reload();
            })
            .catch((error) => {
                console.error("Error deleting training:", error);
                alert("Błąd przy usuwaniu treningu.");
            });
    };

    /**
     * Open delete confirmation modal
     * @param {Object} training - Training object to delete
     */
    const openDeleteModal = (training) => {
        setTrainingToDelete(training);
        setIsDeleteModalOpen(true);
    };

    // Handle no data case
    if (!data) {
        return <div className="no-data-message">Brak danych treningów.</div>;
    }

    const trainings = Array.isArray(data) ? data : [];

    // Sort trainings by start time (earliest first)
    const sortedTrainings = [...trainings].sort(
        (a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf()
    );

    /**
     * Filter trainings based on search query
     */
    const filteredTrainings = sortedTrainings.filter((training) => {
        const searchTerm = searchQuery.toLowerCase();

        if (training.isGroupTraining) {
            const groupName = training.groupName?.toLowerCase() || "";
            const groupDescription = training.groupDescription?.toLowerCase() || "";
            const hallName = training.hallName?.toLowerCase() || "";
            const trainingDate = formatDateTime(training.startTime).toLowerCase();
            const groupTrainingText = "trening grupowy";

            return (
                groupName.includes(searchTerm) ||
                groupDescription.includes(searchTerm) ||
                hallName.includes(searchTerm) ||
                trainingDate.includes(searchTerm) ||
                groupTrainingText.includes(searchTerm)
            );
        }

        const dogName = training.dogDTO?.name?.toLowerCase() || "";
        const dogBreed = training.dogDTO?.breed?.toLowerCase() || "";
        const ownerName = training.dogDTO?.ownerName?.toLowerCase() || "";
        const hallName = training.hallName?.toLowerCase() || "";
        const serviceName = training.serviceName?.toLowerCase() || "";
        const trainingDate = formatDateTime(training.startTime).toLowerCase();

        return (
            dogName.includes(searchTerm) ||
            dogBreed.includes(searchTerm) ||
            ownerName.includes(searchTerm) ||
            hallName.includes(searchTerm) ||
            serviceName.includes(searchTerm) ||
            trainingDate.includes(searchTerm)
        );
    });

    return (
        <div className="confirmed-trainings-wrapper">
            {/* Header with search bar */}
            <div className="confirmed-trainings-header">
                <div></div>
                <div className="search-bar-container">
                    <IoSearchOutline className="search-bar-icon" />
                    <input
                        type="text"
                        placeholder="Wyszukaj treningi..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-bar-input"
                    />
                </div>
            </div>

            {/* Trainer tabs - show only if multiple trainers */}
            {trainersList.length > 1 && (
                <div className="trainer-tabs-container">
                    <div className="trainer-tabs-list">
                        {trainersList.map((trainerItem) => (
                            <button
                                key={trainerItem.id}
                                onClick={() => handleTrainerSelection(trainerItem.id)}
                                className={
                                    trainerItem.id === selectedTrainerId
                                        ? "trainer-tab-button trainer-tab-button--active"
                                        : "trainer-tab-button"
                                }
                            >
                                {trainerItem.firstName}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Single trainer label */}
            {trainersList.length === 1 && trainersList[0] && (
                <div className="single-trainer-header">
                    <h4>{trainersList[0].firstName}</h4>
                </div>
            )}

            {/* Trainings list */}
            <div className="trainings-list-container">
                {filteredTrainings.length === 0 ? (
                    <p className="no-trainings-message">Brak treningów w tym okresie.</p>
                ) : (
                    filteredTrainings.map((training) => (
                        <div
                            key={training.id}
                            className={`training-card ${
                                training.isGroupTraining ? "training-card--group" : ""
                            }`}
                        >
                            {/* Training card header */}
                            <div className="training-card-header">
                                <div
                                    className="training-card-summary"
                                    onClick={() => toggleTrainingDetails(training.id)}
                                >
                                    <div className="training-card-title">
                                        {training.isGroupTraining && (
                                            <FaUsers className="group-training-icon" />
                                        )}
                                        <div className="training-card-name-date">
                                            <span className="training-card-name">
                                                {training.isGroupTraining
                                                    ? training.groupName
                                                    : training.dogDTO?.name}
                                            </span>
                                            <span className="training-card-date">
                                                {formatDateTime(training.startTime)}
                                            </span>
                                        </div>
                                    </div>
                                    <span className="training-card-service">
                                        {training.serviceName || "Trening"}
                                    </span>
                                </div>

                                <div className="training-card-actions">
                                    {!training.isGroupTraining && (
                                        <AiOutlineDelete
                                            className="training-delete-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openDeleteModal(training);
                                            }}
                                        />
                                    )}
                                    <div
                                        className="training-expand-button"
                                        onClick={() => toggleTrainingDetails(training.id)}
                                    >
                                        {expandedTrainingId === training.id ? (
                                            <FaChevronUp className="training-expand-icon" />
                                        ) : (
                                            <FaChevronDown className="training-expand-icon" />
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Expanded training details */}
                            {expandedTrainingId === training.id && (
                                <div className="training-card-details">
                                    <div className="training-details-content">
                                        {training.isGroupTraining ? (
                                            <>
                                                <p>
                                                    <strong>Grupa:</strong> {training.groupName}
                                                </p>
                                                {training.groupDescription && (
                                                    <p>
                                                        <strong>Opis:</strong>{" "}
                                                        {training.groupDescription}
                                                    </p>
                                                )}
                                                <p>
                                                    <strong>Liczba psów:</strong>{" "}
                                                    {training.dogsCount}
                                                </p>
                                                <p>
                                                    <strong>Liczba trenerów:</strong>{" "}
                                                    {training.trainersCount}
                                                </p>
                                                {training.hallName && (
                                                    <p>
                                                        <strong>Sala:</strong> {training.hallName}
                                                    </p>
                                                )}
                                                <p>
                                                    <strong>Czas:</strong>{" "}
                                                    {formatDateTime(training.startTime)} -{" "}
                                                    {dayjs(training.endTime).format("HH:mm")}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p>
                                                    <strong>Rasa:</strong> {training.dogDTO?.breed}
                                                </p>
                                                <p>
                                                    <strong>Przewodnik:</strong>{" "}
                                                    {training.dogDTO?.ownerName}
                                                </p>
                                                <p>
                                                    <strong>Cena:</strong> {training.price}
                                                </p>
                                                {training.hallName && (
                                                    <p>
                                                        <strong>Sala:</strong> {training.hallName}
                                                    </p>
                                                )}
                                                {training.additionalInfo?.additionalInfo && (
                                                    <p>
                                                        <strong>Informacje od klienta:</strong>{" "}
                                                        {training.additionalInfo.additionalInfo}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Delete confirmation modal */}
            {isDeleteModalOpen && trainingToDelete && (
                <DeleteConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={() => handleDeleteTrainingConfirmation(trainingToDelete.id)}
                    itemName={`Trening ${formatDateTime(trainingToDelete.startTime)}`}
                />
            )}
        </div>
    );
};

export default TrainerConfirmedTrainings;